import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { backofficeUrl, T } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import BellSolid from 'src/shared/svg/bellSolid';
import GlobeEuropeSolid from 'src/shared/svg/globeEuropeSolid';
import StoreAltSolid from 'src/shared/svg/storeAltSolid';
import HeadwayWidget from '../toolbar/headwayWidget';
import LanguagePopupMenu from '../toolbar/languagePopupMenu';
import UserPopupMenu from '../toolbar/userPopupMenu';
import { ContanierLanguageMenu, MenuAccountContainer } from '../toolbar/globalsToolbar';
import SidebarPortalPopup from './SidebarPortalPopup';

const FooterContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    gap: 8px;
`;

const AccountButton = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 8px 4px 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #222;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    min-width: 0;
    flex: 1 1 auto;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.04);
    }
`;

const Avatar = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #222;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    text-transform: uppercase;
`;

const AccountLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

const IconGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
`;

const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #555;
    padding: 0;
    border-radius: 6px;
    font-family: inherit;
    position: relative;
    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.06);
        color: #222;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const UserPopupContent = styled.div`
    ${MenuAccountContainer} {
        position: relative;
        top: auto;
        left: auto;
        right: auto;
        bottom: auto;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
    }
`;

const LanguagePopupContent = styled.div`
    ${ContanierLanguageMenu} {
        position: relative;
        top: auto;
        left: auto;
        right: auto;
        bottom: auto;
        display: block;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
    }
`;

// Store selector popup: a small list of connected stores to pick from.
const StorePopupCard = styled.div`
    background: white;
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
    min-width: 260px;
    max-width: calc(100vw - 24px);
    max-height: 380px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const StorePopupTitle = styled.div`
    padding: 12px 16px 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
`;

const StorePopupList = styled.div`
    overflow-y: auto;
`;

const StoreRow = styled.a`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    text-decoration: none;
    color: #222;
    cursor: pointer;
    transition: background 0.1s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.04);
    }

    svg {
        width: 16px;
        height: 16px;
        fill: #555;
        flex-shrink: 0;
    }
`;

const StoreRowText = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1 1 auto;

    strong {
        font-size: 13px;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    small {
        font-size: 11px;
        color: #888;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const HeadwayOverlay = styled.div`
    position: absolute;
    top: -2px;
    right: -2px;
    pointer-events: none;

    .headway-badge {
        position: relative;
    }
`;

interface StoreInfo {
    id: string;
    name: string;
    url: string;
}

const SidebarFooter: FunctionComponent = () => {
    const { data, userInfo, ecommerceSettings } = useAccountData();
    const [isUserOpen, setIsUserOpen] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);
    const [isStoreOpen, setIsStoreOpen] = useState(false);
    const [isBellOpen, setIsBellOpen] = useState(false);

    useEffect(() => {
        if (!isUserOpen && !isLangOpen && !isStoreOpen && !isBellOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsUserOpen(false);
                setIsLangOpen(false);
                setIsStoreOpen(false);
                setIsBellOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isUserOpen, isLangOpen, isStoreOpen, isBellOpen]);

    if (!userInfo) return null;

    const ecommerceName = ecommerceSettings?.ecommerceName || 'NONE';
    const hasStore = ecommerceName.toUpperCase() !== 'NONE';
    const accountStoreUrl = (ecommerceSettings as any)?.definitiveUrl as string | undefined;
    const name = userInfo.name || '';
    const initial = name.charAt(0) || '?';

    // Collect connected stores: the master integration (account ecommerceSettings)
    // plus any extra salesChannels from the multi-channel list. Dedupe by URL.
    const serverChannels =
        ((data as any)?.salesChannels as
            | Array<{ id: string; ecommerceSettings?: { ecommerceName?: string | null; definitiveUrl?: string | null } }>
            | null
            | undefined) ?? [];

    const rawStores: StoreInfo[] = [];
    if (hasStore && accountStoreUrl) {
        rawStores.push({ id: 'master', name: ecommerceName, url: accountStoreUrl });
    }
    for (const c of serverChannels) {
        const name = c.ecommerceSettings?.ecommerceName || '';
        const url = c.ecommerceSettings?.definitiveUrl || '';
        if (!url || name.toUpperCase() === 'NONE') continue;
        rawStores.push({ id: c.id, name: name || 'Store', url });
    }
    const seenUrls = new Set<string>();
    const stores: StoreInfo[] = rawStores.filter((s) => {
        if (seenUrls.has(s.url)) return false;
        seenUrls.add(s.url);
        return true;
    });

    const handleStoreClick = () => {
        if (!hasStore) return;
        // Single store → open directly, multiple → popup to pick.
        if (stores.length === 1) {
            window.open(stores[0].url, '_blank', 'noopener,noreferrer');
            return;
        }
        closeAllPopups();
        setIsStoreOpen(true);
    };

    const closeAllPopups = () => {
        setIsUserOpen(false);
        setIsLangOpen(false);
        setIsStoreOpen(false);
        setIsBellOpen(false);
    };

    const openBell = () => {
        closeAllPopups();
        setIsBellOpen(true);
        const headway = (window as any).Headway;
        if (headway && typeof headway.show === 'function') headway.show();
    };

    const closeBell = () => {
        setIsBellOpen(false);
        const headway = (window as any).Headway;
        if (headway && typeof headway.hide === 'function') headway.hide();
    };

    return (
        <>
            <FooterContainer>
                <AccountButton
                    type='button'
                    onClick={() => {
                        closeAllPopups();
                        setIsUserOpen(true);
                    }}
                    title={T._('Your Account', 'Admin')}
                >
                    <Avatar>{initial}</Avatar>
                    <AccountLabel>{T._('Your Account', 'Admin')}</AccountLabel>
                </AccountButton>

                <IconGroup>
                    <IconButton
                        type='button'
                        onClick={handleStoreClick}
                        disabled={!hasStore}
                        title={!hasStore ? T._('Connect a store first', 'Admin') : T._('Open your store', 'Admin')}
                    >
                        <StoreAltSolid />
                    </IconButton>

                    <IconButton
                        type='button'
                        onClick={() => {
                            closeAllPopups();
                            setIsLangOpen(true);
                        }}
                        title={T._('Language', 'Admin')}
                    >
                        <GlobeEuropeSolid />
                    </IconButton>

                    <IconButton type='button' onClick={openBell} title={T._('Notifications', 'Admin')}>
                        <BellSolid />
                        <HeadwayOverlay>
                            <HeadwayWidget />
                        </HeadwayOverlay>
                    </IconButton>
                </IconGroup>
            </FooterContainer>

            <SidebarPortalPopup open={isUserOpen} onClose={() => setIsUserOpen(false)} style={{ left: 12, bottom: 64 }}>
                <UserPopupContent>
                    <UserPopupMenu
                        nameUser={name}
                        isMenuHidden={false}
                        ecommerceName={ecommerceName}
                        userId={userInfo.userID}
                        onItemClick={() => setIsUserOpen(false)}
                    />
                </UserPopupContent>
            </SidebarPortalPopup>

            <SidebarPortalPopup open={isLangOpen} onClose={() => setIsLangOpen(false)} style={{ left: 12, bottom: 64 }}>
                <LanguagePopupContent>
                    <LanguagePopupMenu isPopupVisible={isLangOpen} />
                </LanguagePopupContent>
            </SidebarPortalPopup>

            <SidebarPortalPopup open={isStoreOpen} onClose={() => setIsStoreOpen(false)} style={{ left: 12, bottom: 64 }}>
                <StorePopupCard>
                    <StorePopupTitle>{T._('Go to store', 'Admin')}</StorePopupTitle>
                    <StorePopupList>
                        {stores.length > 0 ? (
                            stores.map((s) => (
                                <StoreRow
                                    key={s.id}
                                    href={s.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={() => {
                                        setIsStoreOpen(false);
                                    }}
                                >
                                    <StoreAltSolid />
                                    <StoreRowText>
                                        <strong>{s.name}</strong>
                                        <small>{s.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</small>
                                    </StoreRowText>
                                </StoreRow>
                            ))
                        ) : (
                            <StoreRow as='div' style={{ cursor: 'default' }}>
                                <StoreRowText>
                                    <strong>{T._('No stores available', 'Admin')}</strong>
                                    <small>{T._('Connect a channel from Sales channels & Modules', 'Admin')}</small>
                                </StoreRowText>
                            </StoreRow>
                        )}
                    </StorePopupList>
                </StorePopupCard>
            </SidebarPortalPopup>

            {/* Bell: only the backdrop — Headway renders its own panel. */}
            <SidebarPortalPopup open={isBellOpen} onClose={closeBell} />
        </>
    );
};

export default SidebarFooter;
